import { KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { countries } from 'countries-list';
import { from, iif, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, toArray } from 'rxjs/operators';
import {
  LotStatusEnum,
  LotVersionsFragment,
  TrailEventAuthor,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import { environment } from 'src/environments/environment';

import { Version } from '../../types/version.type';

@Component({
  selector: 'app-lot-history',
  templateUrl: './lot-history.component.html',
  styleUrls: ['./lot-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotHistoryComponent {
  versions$: Observable<Version[]> = of([]);

  rawView = false;

  // order is messed up in generated enum
  lotStatuses = [
    LotStatusEnum.Initial,
    LotStatusEnum.Sold,
    LotStatusEnum.Negotiations,
    LotStatusEnum.Won,
    LotStatusEnum.Canceled,
    LotStatusEnum.Unsold,
    LotStatusEnum.NegotiationSold,
    LotStatusEnum.Removed,
  ];
  managerDomain = environment.managerDomain;

  excludeFields = ['production_date_payload'];

  constructor(private readonly translate: TranslateService) {}

  @Input()
  set lot(lot: LotVersionsFragment) {
    this.versions$ = from(lot?.versions ?? []).pipe(
      map(
        (version) =>
          ({
            ...version,
            changeset$: of(version.changeset).pipe(
              switchMap((changeset) => from(Object.entries(changeset))),
              map(([key, value]) => ({ key, value })),
              filter(({ key }) => !this.excludeFields.includes(key)),
              toArray<KeyValue<string, any>>(),
              switchMap((changes: KeyValue<string, any>[]) =>
                iif(
                  () => changes.length === 1 && changes[0].key === 'updated_at',
                  of(changes),
                  from(changes).pipe(
                    filter(({ key }) => key !== 'updated_at'),
                    toArray(),
                  ),
                ),
              ),
              shareReplay(1),
            ),
          } as Version),
      ),
      toArray(),
      shareReplay(1),
    );
  }

  extractIcons(iconChangeStr: string): string {
    return iconChangeStr
      .replace(/\{|\}/g, '')
      .split(',')
      .filter((icon) => Boolean(icon))
      .map((icon) => this.translate.instant(`LOT_TRANSLATION.ICONS_TOOLTIP.${icon}`))
      .join(', ');
  }

  getUserCountryName(countryIso: string): string {
    return countries[countryIso?.toUpperCase()]?.name ?? '';
  }

  extractImages(imagesChangeStr: string): string[] {
    return JSON.parse(imagesChangeStr);
  }
}
